import { MARKETING, HOMEPAGE, STATS_UPDATED_AT } from "@/lib/siteStats";
import { formatCount, showCount } from "@/lib/statsFormat";

// One decimal place, no cap — matches the tile's own original toFixed(1),
// kept as a named function (not inline) so the count-up can call it too.
function formatPercent(n: number): string {
  return `${n.toFixed(1)}%`;
}

// Stats for the Digital Marketing page. Values come from the single
// public/stats.json snapshot — see lib/siteStats.ts.
//
// Source: the lead pipeline in defaultdb — see StatsController::marketingStats.
//
// These are Vilva's OWN inbound leads, not results delivered for clients, so
// every label here has to stay true under that reading: "Leads Captured" and
// "Lead-to-Customer Rate" describe Vilva's funnel and are honest. "Leads
// Generated" would imply client outcomes and is not. Nothing in the estate
// records per-client marketing results, so no tile here can claim them.

export const MARKETING_UPDATED_AT = STATS_UPDATED_AT;

// ---------------------------------------------------------------------------
// TEMPORARY — preview figures, delete once the backend is deployed.
// Same NODE_ENV guard as lib/verificationStats.ts: invented numbers render on
// the dev server so the section can be reviewed, and are unreachable in a
// production build.
const SAMPLE = { leads_captured: 2431, channels_tracked: 12, conversion_rate: 18.6 };

const SOURCE =
  MARKETING.leads_captured > 0 || process.env.NODE_ENV === "production"
    ? MARKETING
    : SAMPLE;
// ---------------------------------------------------------------------------

// Renders unconditionally — see the matching note in lib/verificationStats.ts.

// The backend sends null when it can't identify which lead statuses mean "won".
// Rather than show a gap, the tile is swapped for a figure that's always
// available — so the band is always four across, and never four across because
// a percentage got invented to fill the slot.
const conversionTile =
  SOURCE.conversion_rate != null && SOURCE.conversion_rate > 0
    ? {
        value: formatPercent(SOURCE.conversion_rate),
        // Percentages aren't counts — the tooltip would only repeat what's
        // already shown, so 0 suppresses it (see StatNumber). `target` carries
        // the real percentage separately, purely for the count-up.
        exact: 0,
        target: SOURCE.conversion_rate,
        format: formatPercent,
        label: "Lead-to-Customer Rate",
      }
    : {
        value: showCount(HOMEPAGE.projects_delivered),
        exact: HOMEPAGE.projects_delivered,
        target: HOMEPAGE.projects_delivered,
        format: formatCount,
        label: "Campaigns & Projects Delivered",
      };

export const MARKETING_STATS = [
  {
    value: showCount(SOURCE.leads_captured),
    exact: SOURCE.leads_captured,
    target: SOURCE.leads_captured,
    format: formatCount,
    label: "Leads Captured",
  },
  {
    value: showCount(SOURCE.channels_tracked),
    exact: SOURCE.channels_tracked,
    target: SOURCE.channels_tracked,
    format: formatCount,
    label: "Channels Tracked",
  },
  conversionTile,
  {
    value: showCount(HOMEPAGE.happy_customers),
    exact: HOMEPAGE.happy_customers,
    target: HOMEPAGE.happy_customers,
    format: formatCount,
    label: "Businesses Served",
  },
];
